import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useShift } from '@/hooks/useShift';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { MedicalDisclaimerBanner } from '@/components/legal/MedicalDisclaimerBanner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import {
  Activity,
  Utensils,
  Droplets,
  Dumbbell,
  FileText,
  Camera,
  ImageOff,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { compressImageSafe } from '@/lib/image-utils';
import { usePatientVitalRanges } from '@/hooks/usePatientVitalRanges';
import { checkVitalAlert } from '@/lib/vital-alert';

type AdhocTaskType = 'vital_sign' | 'food' | 'hygiene' | 'physical_activity' | 'other';
type VitalType = 'blood_pressure' | 'glucose' | 'temperature' | 'oxygen_saturation' | 'weight' | 'heart_rate';
type HygieneType = 'bath' | 'diaper_change' | 'dressing';
type FoodAcceptance = 'nothing' | 'little' | 'half' | 'almost_all' | 'all';

export default function AdHocTaskPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: patientId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { activeShift } = useShift();
  const { getRangeForVitalType } = usePatientVitalRanges(patientId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Internationalized lists
  const TASK_TYPES: { value: AdhocTaskType; label: string; icon: React.ReactNode }[] = [
    { value: 'vital_sign', label: t('carePlan.vitals'), icon: <Activity className="w-5 h-5" /> },
    { value: 'food', label: t('carePlan.food'), icon: <Utensils className="w-5 h-5" /> },
    { value: 'hygiene', label: t('carePlan.hygiene'), icon: <Droplets className="w-5 h-5" /> },
    { value: 'physical_activity', label: t('carePlan.physicalActivity'), icon: <Dumbbell className="w-5 h-5" /> },
    { value: 'other', label: t('carePlan.otherTasks'), icon: <FileText className="w-5 h-5" /> },
  ];

  const VITAL_TYPES: { value: VitalType; label: string; unit: string; hasSecondary?: boolean }[] = [
    { value: 'blood_pressure', label: t('vitals.bloodPressure'), unit: 'mmHg', hasSecondary: true },
    { value: 'glucose', label: t('vitals.glucose'), unit: 'mg/dL' },
    { value: 'temperature', label: t('vitals.temperature'), unit: '°C' },
    { value: 'oxygen_saturation', label: t('vitals.oxygenSaturation'), unit: '%' },
    { value: 'weight', label: t('vitals.weight'), unit: 'kg' },
    { value: 'heart_rate', label: t('vitals.heartRate'), unit: 'bpm' },
  ];

  const HYGIENE_TYPES: { value: HygieneType; label: string }[] = [
    { value: 'bath', label: t('hygiene.bath') },
    { value: 'diaper_change', label: t('hygiene.diaperChange') },
    { value: 'dressing', label: t('hygiene.dressing') },
  ];

  const FOOD_ACCEPTANCE: { value: FoodAcceptance; label: string }[] = [
    { value: 'nothing', label: t('food.acceptance.nothing') },
    { value: 'little', label: t('food.acceptance.little') },
    { value: 'half', label: t('food.acceptance.half') },
    { value: 'almost_all', label: t('food.acceptance.almostAll') },
    { value: 'all', label: t('food.acceptance.all') },
  ];

  const [taskType, setTaskType] = useState<AdhocTaskType | null>(null);
  const [vitalType, setVitalType] = useState<VitalType | null>(null);
  const [hygieneType, setHygieneType] = useState<HygieneType | null>(null);
  const [taskName, setTaskName] = useState('');
  const [vitalValue, setVitalValue] = useState('');
  const [vitalValueSecondary, setVitalValueSecondary] = useState('');
  const [foodAcceptance, setFoodAcceptance] = useState<FoodAcceptance | null>(null);
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { file: compressedFile, previewUrl } = await compressImageSafe(file, {
        maxWidth: 1600,
        maxHeight: 1600,
        quality: 0.7
      });

      setPhotoFile(compressedFile);
      setPhotoPreview(previewUrl);
    } catch (error) {
      console.error('Error processing photo:', error);
    }
  };

  const uploadPhoto = async (): Promise<string | null> => {
    if (!photoFile || !user || !patientId) return null;

    const fileExt = photoFile.name.split('.').pop();
    // First folder MUST be patient_id (shift-photos RLS policy).
    const fileName = `${patientId}/${user.id}/${Date.now()}.${fileExt}`;

    const { error } = await supabase.storage
      .from('shift-photos')
      .upload(fileName, photoFile, { contentType: photoFile.type || 'image/jpeg', upsert: false });

    if (error) {
      console.error('Error uploading photo:', error);
      toast.error(t('common.error'));
      return null;
    }

    const { data } = supabase.storage
      .from('shift-photos')
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  const getAdhocName = (): string => {
    switch (taskType) {
      case 'vital_sign':
        return VITAL_TYPES.find(v => v.value === vitalType)?.label || t('carePlan.vitals');
      case 'food':
        return taskName || t('carePlan.food');
      case 'hygiene':
        return HYGIENE_TYPES.find(h => h.value === hygieneType)?.label || t('carePlan.hygiene');
      case 'physical_activity':
        return taskName || t('carePlan.physicalActivity');
      case 'other':
        return taskName || t('taskDefault.adhocTask');
      default:
        return t('taskDefault.adhocTask');
    }
  };

  const isFormValid = (): boolean => {
    if (!taskType) return false;

    switch (taskType) {
      case 'vital_sign': {
        if (!vitalType || !vitalValue) return false;
        const cfg = VITAL_TYPES.find(v => v.value === vitalType);
        if (cfg?.hasSecondary && !vitalValueSecondary) return false;
        return true;
      }
      case 'food':
        return !!foodAcceptance;
      case 'hygiene':
        return !!hygieneType;
      case 'physical_activity':
        return !!taskName;
      case 'other':
        return !!taskName;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!user || !patientId || !taskType) return;

    setIsSubmitting(true);
    try {
      // Require both values for two-value vital signs (e.g. blood pressure)
      if (taskType === 'vital_sign' && vitalType) {
        const cfg = VITAL_TYPES.find(v => v.value === vitalType);
        if (cfg?.hasSecondary && (!vitalValue || !vitalValueSecondary)) {
          toast.error(t('executeTask.bothValuesRequired', { defaultValue: 'Preencha os dois valores (sistólica e diastólica).' }));
          setIsSubmitting(false);
          return;
        }
      }

      // Upload photo if present
      const photoUrl = await uploadPhoto();

      // Check if vital is out of range by looking up patient vital ranges
      let isAlert = false;
      if (taskType === 'vital_sign' && vitalType && vitalValue) {
        try {
          const { data: vitalRange } = await supabase
            .from('patient_vital_ranges')
            .select('min_value, max_value, min_value_secondary, max_value_secondary')
            .eq('patient_id', patientId)
            .eq('vital_type', vitalType)
            .maybeSingle();

          isAlert = checkVitalAlert(
            parseFloat(vitalValue),
            vitalValueSecondary ? parseFloat(vitalValueSecondary) : null,
            vitalRange
          );
        } catch (err) {
          console.warn('Error checking vital limits for ad-hoc task:', err);
        }
      }

      // Create ad-hoc execution (task_id is NULL)
      const { error } = await supabase
        .from('task_executions')
        .insert({
          patient_id: patientId,
          executed_by: user.id,
          shift_id: activeShift?.id || null,
          task_id: null, // No planned task
          adhoc_task_type: taskType,
          adhoc_name: getAdhocName(),
          adhoc_vital_type: taskType === 'vital_sign' ? vitalType : null,
          vital_value: vitalValue ? parseFloat(vitalValue) : null,
          vital_value_secondary: vitalValueSecondary ? parseFloat(vitalValueSecondary) : null,
          food_acceptance: foodAcceptance,
          notes: notes || null,
          photo_url: photoUrl,
          is_sporadic: true, // Mark as ad-hoc for metrics exclusion
          is_alert: isAlert,
        });

      if (error) throw error;

      toast.success(getAdhocName(), {
        description: t('adhocTask.success'),
      });

      if (isAlert) {
        toast.warning(t('executeTask.alertWarning'));
      }

      navigate(`/app/caregiver/${patientId}`);
    } catch (error) {
      console.error('Error saving ad-hoc task:', error);
      toast.error(t('common.error'), {
        description: t('adhocTask.errorSaving'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const selectedVitalConfig = VITAL_TYPES.find(v => v.value === vitalType);

  return (
    <AppLayout
      title={t('adhocTask.title')}
      showBack
      onBack={() => navigate(`/app/caregiver/${patientId}`)}
    >
      <div className="p-4 space-y-4 pb-24">
        <MedicalDisclaimerBanner />
        {/* Task Type Selection */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('adhocTask.selectType')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              {TASK_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => {
                    setTaskType(type.value);
                    setVitalType(null);
                    setHygieneType(null);
                    setFoodAcceptance(null);
                    setTaskName('');
                  }}
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all",
                    taskType === type.value
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-card hover:border-primary/50"
                  )}
                >
                  {type.icon}
                  <span className="text-xs font-medium text-center">{type.label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Dynamic Fields based on task type */}
        {taskType === 'vital_sign' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('adhocTask.vitalType')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup value={vitalType || ''} onValueChange={(v) => setVitalType(v as VitalType)}>
                <div className="grid grid-cols-2 gap-2">
                  {VITAL_TYPES.map((vital) => (
                    <div key={vital.value} className="flex items-center space-x-2">
                      <RadioGroupItem value={vital.value} id={vital.value} />
                      <Label htmlFor={vital.value} className="text-sm cursor-pointer">
                        {vital.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>

              {vitalType && (
                <div className="space-y-3 pt-2 border-t">
                  <div className="space-y-2">
                    <Label>{t('executeTask.measurement')}{selectedVitalConfig?.unit ? ` (${selectedVitalConfig.unit})` : ''}</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="11"
                        value={vitalValue}
                        onChange={(e) => setVitalValue(e.target.value)}
                      />
                      {selectedVitalConfig?.hasSecondary && (
                        <Input
                          type="number"
                          placeholder="8"
                          value={vitalValueSecondary}
                          onChange={(e) => setVitalValueSecondary(e.target.value)}
                        />
                      )}
                    </div>
                    {(() => {
                      const range = getRangeForVitalType(vitalType);
                      if (!range) return null;
                      if (range.vital_type === 'blood_pressure') {
                        return (range.min_value != null && range.max_value != null) ? (
                          <p className="text-xs text-muted-foreground">
                            {t('vitalRanges.expectedRange', { defaultValue: 'Faixa esperada' })}: {range.min_value}–{range.max_value}
                            {range.min_value_secondary != null && range.max_value_secondary != null && (
                              <> / {range.min_value_secondary}–{range.max_value_secondary}</>
                            )} {range.unit || 'mmHg'}
                          </p>
                        ) : null;
                      }
                      return (range.min_value != null && range.max_value != null) ? (
                        <p className="text-xs text-muted-foreground">
                          {t('vitalRanges.expectedRange', { defaultValue: 'Faixa esperada' })}: {range.min_value} – {range.max_value} {range.unit || ''}
                        </p>
                      ) : null;
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {taskType === 'food' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('adhocTask.foodDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('adhocTask.mealName')}</Label>
                <Input
                  placeholder={t('adhocTask.mealNamePlaceholder')}
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('executeTask.acceptance')}</Label>
                <div className="flex flex-wrap gap-2">
                  {FOOD_ACCEPTANCE.map((acceptance) => (
                    <button
                      key={acceptance.value}
                      onClick={() => setFoodAcceptance(acceptance.value)}
                      className={cn(
                        "px-4 py-2 rounded-full text-sm font-medium transition-all",
                        foodAcceptance === acceptance.value
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-muted/80"
                      )}
                    >
                      {acceptance.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {taskType === 'hygiene' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('adhocTask.hygieneType')}</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup value={hygieneType || ''} onValueChange={(v) => setHygieneType(v as HygieneType)}>
                <div className="space-y-2">
                  {HYGIENE_TYPES.map((hygiene) => (
                    <div key={hygiene.value} className="flex items-center space-x-2">
                      <RadioGroupItem value={hygiene.value} id={hygiene.value} />
                      <Label htmlFor={hygiene.value} className="cursor-pointer">
                        {hygiene.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        )}

        {taskType === 'physical_activity' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('adhocTask.activityDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('adhocTask.activityName')}</Label>
                <Input
                  placeholder={t('adhocTask.activityNamePlaceholder')}
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {taskType === 'other' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('adhocTask.otherDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('adhocTask.taskName')}</Label>
                <Input
                  placeholder={t('adhocTask.taskNamePlaceholder')}
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes & Photo (shown when task type is selected) */}
        {taskType && (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('executeTask.observations')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder={t('executeTask.observationsPlaceholder')}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('executeTask.photo')}</CardTitle>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  ref={fileInputRef}
                  onChange={handlePhotoChange}
                  className="hidden"
                />

                {photoPreview ? (
                  <div className="relative">
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-full aspect-video object-cover rounded-lg"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      className="absolute bottom-2 right-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {t('executeTask.retake')}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full h-32 flex flex-col gap-2"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera className="w-8 h-8" />
                    <span>{t('executeTask.takePhoto')}</span>
                  </Button>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Submit Button */}
        <Button
          className="w-full"
          size="lg"
          onClick={handleSubmit}
          disabled={!isFormValid() || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('common.saving')}
            </>
          ) : (
            t('adhocTask.register')
          )}
        </Button>
      </div>
    </AppLayout>
  );
}
